// Ultimate Card Game (UCG) - Card & Hand Management System
//
// Neustart "Marine Null": drawing is player-initiated (a DRAW command per
// empty slot, see js/commands/commandProcessor.js) instead of a passive
// per-second timer, and plasma is the only resource - see
// js/data/balance.js and NEUSTART_PLAN.html Phase 3 for the reasoning.

import { CARDS_DATA, getCardByName } from '../data/cards.js';
import { DECKS_DATA } from '../data/decks.js';
import { getCustomDeckById } from '../data/customDecks.js';
import { UnseededRNG } from './rng.js';
import { BALANCE } from '../data/balance.js';
import { MODULE_MAX } from './modules.js';
import { hasCustomSpawnSound } from '../data/unitSounds.js';

// Monotonic counter for hand-card instance ids - see unitIdCounter in unit.js.
let cardInstanceCounter = 0;

// Drilling: how long a freshly-detected triplet sits visible in hand before
// it actually fuses - see checkDrilling()/_resolvePendingMelt() in
// CardSystem below. Snappy 0.35s convergence animation.
const DRILLING_MELD_DELAY = 0.35;

// Sul caste progression - single source of truth shared by checkSymbiosis()
// (the actual fusion trigger) and getFusionInfo() (the zoom-tooltip lookup
// in js/ui/hud.js), so the two can never disagree about what comes after
// what.
const CASTE_ORDER = ['Loaf', 'Pillar', 'Lock', 'Vulture'];
const NEXT_CASTE_MAP = {
  'Loaf': 'Pillar',
  'Pillar': 'Lock',
  'Lock': 'Vulture',
  'Vulture': 'Symbiosis'
};

// What a networked opponent's hand slot looks like locally before they've
// actually played it - occupies the slot identically to a real draw, but
// carries no real identity. CommandProcessor._play() replaces this with the
// real card (looked up by the name the PLAY command reveals) the instant
// it's actually played - see js/commands/commandProcessor.js and
// MULTIPLAYER_PLAN.md section 7.
export const HIDDEN_CARD = { name: '???', tier: 0, rarity: 'Hidden', hidden: true };

// What MY OWN hand slot looks like when a DRAW command has already
// deterministically claimed it and paid for it, but the card's actual
// identity hasn't arrived from the server yet (RemoteDrawBuffer underrun -
// a network hiccup, not a rejection). update() below retries resolving it
// every tick until the buffer catches up. Slot occupancy and the plasma
// spend are never delayed by this - only which card it turns out to be -
// which is what keeps both clients' hand-slot bookkeeping identical even
// when only one of them temporarily doesn't know a card's name yet.
export const PENDING_CARD = { name: '···', tier: 0, rarity: 'Pending', pending: true };

// Expands a deck (preset id, custom deck id, or explicit card-name array -
// the same argument shapes setDeck() below accepts) into the {name, tier, rarity}
// list js/ui/onlineLobby.js submits over the WebSocket protocol
// (server/matchMaker.js/deckValidator.js) - the server only needs enough
// to validate the deck and resolve it through its own CardSystem, never
// full card stats/art.
function extractCardNamesFromDeckSource(source) {
  if (!source || typeof source !== 'object') return [];
  if (source.common === 'all') {
    return CARDS_DATA.map(c => c.name);
  }
  const tierNames = [
    ...(Array.isArray(source.common) ? source.common : []),
    ...(Array.isArray(source.uncommon) ? source.uncommon : []),
    ...(Array.isArray(source.epic) ? source.epic : [])
  ];
  if (tierNames.length > 0) return tierNames;
  if (Array.isArray(source.cards)) {
    return source.cards.map(item => (typeof item === 'string' ? item : item?.name || '')).filter(Boolean);
  }
  if (source.cards && typeof source.cards === 'object') {
    const names = [];
    for (const [cardName, qty] of Object.entries(source.cards)) {
      const n = Number(qty) || 0;
      for (let i = 0; i < n; i++) names.push(cardName);
    }
    return names;
  }
  return [];
}

export function buildNetworkDeckPayload(deckIdOrCustomList) {
  let cardNames = [];
  if (typeof deckIdOrCustomList === 'string') {
    const preset = DECKS_DATA[deckIdOrCustomList];
    if (preset) {
      cardNames = extractCardNamesFromDeckSource(preset);
    } else {
      const custom = getCustomDeckById(deckIdOrCustomList);
      const fallback = DECKS_DATA['Standard'];
      const customNames = extractCardNamesFromDeckSource(custom);
      cardNames = customNames.length > 0 ? customNames : extractCardNamesFromDeckSource(fallback);
    }
  } else if (Array.isArray(deckIdOrCustomList)) {
    cardNames = deckIdOrCustomList;
  } else if (deckIdOrCustomList && typeof deckIdOrCustomList === 'object') {
    const extracted = extractCardNamesFromDeckSource(deckIdOrCustomList);
    if (extracted.length > 0) {
      cardNames = extracted;
    } else if (typeof deckIdOrCustomList.id === 'string') {
      return buildNetworkDeckPayload(deckIdOrCustomList.id);
    }
  }

  const deck = [];
  for (const name of cardNames) {
    const cardName = typeof name === 'string' ? name : name?.name;
    const found = getCardByName(cardName);
    if (found) deck.push({ name: found.name, tier: found.tier, rarity: found.rarity });
  }
  return deck.length > 0 ? deck : CARDS_DATA.map(c => ({ name: c.name, tier: c.tier, rarity: c.rarity }));
}

// Resolves a deck id (preset, custom deck, or a raw name list) to the
// actual CARDS_DATA entries it plays with. Only ever yields enabled cards:
// a disabled card must never become drawable just because a deck payload
// named it or came back empty (see CLAUDE.md convention 6 on the legacy
// archive).
export function resolveDeckCards(deckIdOrCustomList) {
  let cardNames = [];
  if (typeof deckIdOrCustomList === 'string') {
    const preset = DECKS_DATA[deckIdOrCustomList];
    if (preset) {
      cardNames = extractCardNamesFromDeckSource(preset);
    } else {
      const custom = getCustomDeckById(deckIdOrCustomList);
      const fallback = DECKS_DATA['Standard'];
      const customNames = extractCardNamesFromDeckSource(custom);
      cardNames = customNames.length > 0 ? customNames : extractCardNamesFromDeckSource(fallback);
    }
  } else if (Array.isArray(deckIdOrCustomList)) {
    if (
      deckIdOrCustomList.length > 0 &&
      deckIdOrCustomList.every(item => typeof item === 'object' && item !== null && (item.race || item.name) && typeof item.cost === 'number')
    ) {
      return deckIdOrCustomList;
    }
    cardNames = deckIdOrCustomList.map(item => (typeof item === 'string' ? item : item?.name || ''));
  } else if (deckIdOrCustomList && typeof deckIdOrCustomList === 'object') {
    const extracted = extractCardNamesFromDeckSource(deckIdOrCustomList);
    if (extracted.length > 0) {
      cardNames = extracted;
    } else if (typeof deckIdOrCustomList.id === 'string') {
      return resolveDeckCards(deckIdOrCustomList.id);
    }
  }

  const fullDeck = [];
  for (const name of cardNames) {
    const found = getCardByName(name);
    if (found && found.enabled === true) fullDeck.push(found);
  }
  if (fullDeck.length > 0) return fullDeck;

  if (deckIdOrCustomList?.id && DECKS_DATA[deckIdOrCustomList.id]) {
    return resolveDeckCards(deckIdOrCustomList.id);
  }

  const fallbackPreset = DECKS_DATA['Standard'];
  if (fallbackPreset) {
    const fallbackCards = [...fallbackPreset.common, ...fallbackPreset.uncommon, ...fallbackPreset.epic]
      .map(n => getCardByName(n))
      .filter(c => c && c.enabled === true);
    if (fallbackCards.length > 0) return fallbackCards;
  }

  return CARDS_DATA.filter(c => c.enabled === true && !c.isCampaignOnly);
}

// THE one place that decides which faction's base a deck puts on the field.
//
// This is a lockstep-critical value, not a cosmetic one: getBaseTurretX()/
// getBaseTurretY() in js/engine/battlefield.js read base.race to place the
// base turret (Sul fires from the apex sphere with barrelLen 6, Human from
// BASE_TURRET_DX/DY with barrelLen 32), so if the two clients disagree
// about a base's race their turret shots leave different coordinates with
// different flight times, land on different ticks, and the match desyncs
// beyond recovery the moment anything walks into base range.
//
// It therefore has exactly one implementation, called by everyone who
// needs the answer: setDeck() below applies it to the local base, and
// js/ui/onlineLobby.js sends its result to the server so the opponent
// builds the identical base. It must never be re-derived anywhere else -
// least of all guessed at from a deck id server-side, which is what the
// PHP endpoints used to do. See docs/INVARIANTS.md convention 4.
export function deriveBaseRace(deckIdOrCustomList, resolvedDeck = null) {
  const deck = resolvedDeck || resolveDeckCards(deckIdOrCustomList);

  // Module override rule:
  // If at least one Human module is selected in the deck, the base is ALWAYS Human.
  // Overrules the faction majority rule and prevents Human industrial steel
  // module towers from deploying onto the organic Sul base.
  if (deck && deck.some(c => c && (c.type === 'Module' || c.isModule) && (!c.race || c.race === 'Human'))) {
    return 'Human';
  }

  let race = 'Human';
  if (typeof deckIdOrCustomList === 'string') {
    const preset = DECKS_DATA[deckIdOrCustomList];
    if (preset && preset.race) {
      return preset.race;
    } else if (deckIdOrCustomList.toLowerCase().includes('sul')) {
      return 'Sul';
    }
  } else if (deckIdOrCustomList && typeof deckIdOrCustomList === 'object' && !Array.isArray(deckIdOrCustomList)) {
    if (typeof deckIdOrCustomList.id === 'string') {
      const preset = DECKS_DATA[deckIdOrCustomList.id];
      if (preset && preset.race) return preset.race;
      if (deckIdOrCustomList.id.toLowerCase().includes('sul')) return 'Sul';
    }
    if (typeof deckIdOrCustomList.race === 'string') {
      return deckIdOrCustomList.race;
    }
  }
  // Majority rule over resolved deck cards (docs/BIO_PLASMA_PLAN.md §4):
  // 1. Only actual on-field units and structures are counted (c.type === 'Unit' || c.type === 'Structure').
  // 2. The faction with the most such cards wins.
  // 3. Tie-breaker: higher cost sum of counted cards.
  // 4. Fallback on remaining tie: 'Human'.
  let sulCount = 0;
  let humanCount = 0;
  let sulCost = 0;
  let humanCost = 0;

  for (const c of deck) {
    if (!c) continue;
    if (c.type === 'Unit' || c.type === 'Structure') {
      const cost = c.cost || 0;
      if (c.race === 'Sul') {
        sulCount++;
        sulCost += cost;
      } else {
        humanCount++;
        humanCost += cost;
      }
    }
  }

  if (sulCount > humanCount) {
    race = 'Sul';
  } else if (humanCount > sulCount) {
    race = 'Human';
  } else if (sulCost > humanCost) {
    race = 'Sul';
  } else {
    race = 'Human';
  }
  return race;
}

// Two-Stage Tiered Draw: picks a card weighted by rarity, then uniform within that rarity.
// Weights are normalized dynamically over the available/non-empty rarity pools.
export function pickWeightedCard(cards, weights = BALANCE.rarityWeights, rng = UnseededRNG) {
  if (!cards || cards.length === 0) return null;
  if (cards.length === 1) return cards[0];

  const byRarity = new Map();
  for (const card of cards) {
    const rawRarity = card.rarity || (card.tier === 1 ? 'Common' : (card.tier === 2 ? 'Uncommon' : (card.tier === 3 ? 'Epic' : 'Common')));
    const key = rawRarity.charAt(0).toUpperCase() + rawRarity.slice(1).toLowerCase();
    if (!byRarity.has(key)) byRarity.set(key, []);
    byRarity.get(key).push(card);
  }

  const activeEntries = [];
  let totalWeight = 0;
  for (const [rarity, pool] of byRarity.entries()) {
    if (pool.length > 0) {
      const weight = (weights && weights[rarity] !== undefined)
        ? weights[rarity]
        : (rarity === 'Common' ? 54 : (rarity === 'Uncommon' ? 30 : (rarity === 'Rare' ? 13 : 3)));
      if (weight > 0) {
        activeEntries.push({ rarity, pool, weight });
        totalWeight += weight;
      }
    }
  }

  if (activeEntries.length === 0 || totalWeight <= 0) {
    return rng.pick(cards);
  }

  let roll = rng.next() * totalWeight;
  let chosenPool = activeEntries[0].pool;
  for (const entry of activeEntries) {
    if (roll < entry.weight) {
      chosenPool = entry.pool;
      break;
    }
    roll -= entry.weight;
  }

  return rng.pick(chosenPool);
}

// Resolves a spell's target unit by id, exactly as transmitted in the
// command - never from battlefield.selectedUnit. See
// docs/INVARIANTS.md #1: that field is local UI-click state, never sent
// over the network, so both lockstep peers must agree on the target via
// this id or they diverge.
function resolveSpellTarget(ctx) {
  return ctx.targetUnitId != null ? ctx.battlefield.getUnitById(ctx.targetUnitId) : null;
}

// Sets a team's hand-card cost discount/penalty - see CardSystem.discounts.
function applyDiscount(discounts, team, multiplier, durationMs) {
  discounts[team].multiplier = multiplier;
  discounts[team].timer = durationMs;
}

// One handler per card.spellEffect string, dispatched from
// CardSystem.executeSpell() below. `ctx` is a plain object built fresh per
// call - it deliberately does NOT expose battlefield.selectedUnit as
// something a handler can reach for casually; a handler that needs a
// target must call resolveSpellTarget(ctx) (backed by ctx.targetUnitId).
// Reaching past ctx into ctx.battlefield.selectedUnit directly is possible
// but visibly deviates from every other handler here - see
// docs/CARD_AUTHORING_GUIDE.md "Neue Spell-Karte" for the checklist when
// adding a new entry.
const SPELL_HANDLERS = {
  grant_minerals_100: (ctx) => { ctx.economy.resources[ctx.team].minerals += 100; },
  grant_minerals_300: (ctx) => { ctx.economy.resources[ctx.team].minerals += 300; },
  grant_minerals_20: (ctx) => { ctx.economy.resources[ctx.team].minerals += 20; },
  grant_minerals_50: (ctx) => { ctx.economy.resources[ctx.team].minerals += 50; },

  grant_plasma_100: (ctx) => {
    const res = ctx.economy.resources[ctx.team];
    res.plasma = Math.min(res.maxPlasma, res.plasma + 100);
  },
  grant_plasma_75: (ctx) => {
    const res = ctx.economy.resources[ctx.team];
    res.plasma = Math.min(res.maxPlasma, res.plasma + 75);
  },
  grant_plasma_30: (ctx) => {
    const res = ctx.economy.resources[ctx.team];
    res.plasma = Math.min(res.maxPlasma, res.plasma + 30);
  },

  concentration: (ctx) => {
    const cs = ctx.cardSystem;
    if (!cs) return;
    for (let s = 0; s < cs.maxHandSize; s++) {
      cs.hands[ctx.team][s] = null;
    }
    cs.drawBatch(ctx.team, cs.maxHandSize, { allowEpicInSlot: 0 });
    cs.checkDrilling(ctx.team);
    cs.lastConcentrationSlot = { team: ctx.team, slot: ctx.originSlot ?? 0 };
    cs.concentrationSeq = (cs.concentrationSeq || 0) + 1;
    if (ctx.team === (ctx.game?.myTeam || 'western')) {
      ctx.audio?.playCardDraw?.();
    }
  },

  mental_extraction: (ctx) => {
    const cs = ctx.cardSystem;
    if (!cs) return;

    // 1. Discard entire own hand (all slots)
    for (let s = 0; s < cs.maxHandSize; s++) {
      cs.hands[ctx.team][s] = null;
    }

    const oppHands = cs.getOpponentHands(ctx.team);

    if (oppHands.length > 1) {
      // 2v2 / Multi-Opponent path:
      // Collect all non-pending, non-hidden opponent cards across hands
      const pool = [];
      for (const h of oppHands) {
        for (const c of h) {
          if (c && !c.pending && !c.hidden) {
            pool.push(c);
          }
        }
      }
      if (pool.length > 0) {
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = ctx.rng?.int ? ctx.rng.int(0, i + 1) : Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const countToTake = Math.min(cs.maxHandSize, shuffled.length);
        for (let s = 0; s < countToTake; s++) {
          const src = shuffled[s];
          const cardData = src.name && src.name !== '···' ? (getCardByName(src.name) || src) : src;
          cs.hands[ctx.team][s] = {
            ...cardData,
            slot: s,
            instanceId: 'card_' + (cardInstanceCounter++)
          };
          delete cs.hands[ctx.team][s].pending;
          delete cs.hands[ctx.team][s].hidden;
        }
      }
    } else {
      // 1v1 path: exact mirror of opponent's hand slots (including empty slots)
      const oppHand = oppHands[0] || [];
      for (let s = 0; s < cs.maxHandSize; s++) {
        const oppCard = oppHand[s];
        if (oppCard && !oppCard.pending) {
          const cardData = oppCard.name && oppCard.name !== '···' ? (getCardByName(oppCard.name) || oppCard) : oppCard;
          cs.hands[ctx.team][s] = {
            ...cardData,
            slot: s,
            instanceId: 'card_' + (cardInstanceCounter++)
          };
          delete cs.hands[ctx.team][s].pending;
          delete cs.hands[ctx.team][s].hidden;
        } else {
          cs.hands[ctx.team][s] = null;
        }
      }
    }

    cs.checkDrilling(ctx.team);
    cs.lastMentalExtractionSlot = { team: ctx.team, slot: ctx.originSlot ?? 0 };
    cs.mentalExtractionSeq = (cs.mentalExtractionSeq || 0) + 1;

    if (ctx.team === (ctx.game?.myTeam || 'western')) {
      if (ctx.audio?.playMentalExtraction) {
        ctx.audio.playMentalExtraction();
      } else {
        ctx.audio?.playCardDraw?.();
      }
    }
  },

  fodder: (ctx) => {
    const b = ctx.battlefield;
    const team = ctx.team;
    const base = b.bases[team];
    const dividerX = (b.territoryDivider !== undefined && b.territoryDivider !== null)
      ? b.territoryDivider
      : (b.width / 2);

    // 10 px towards own base (Option 2)
    const targetX = team === 'western' ? (dividerX - 10) : (dividerX + 10);
    const clampedX = team === 'western'
      ? Math.max(base.x + 130, Math.min(targetX, b.width - 160))
      : Math.min(base.x - 130, Math.max(targetX, 160));

    const laneTop = b.laneY + 40;
    const usableHeight = b.laneHeight - 80;
    const step = usableHeight / 7;

    const baseMarine = getCardByName('Marine');
    if (!baseMarine) return;

    const fodderMarineCard = {
      ...baseMarine,
      spriteRecolor: 'gold'
    };

    for (let i = 0; i < 8; i++) {
      const depthStagger = (i % 2 === 0 ? -20 : 20);
      const posX = clampedX + (team === 'western' ? depthStagger : -depthStagger) + ctx.rng.range(-6, 6);
      const posY = Math.max(b.laneY + 35, Math.min(b.laneY + b.laneHeight - 35, laneTop + i * step + ctx.rng.range(-4, 4)));
      const duration = 1.6 + ctx.rng.range(0.0, 0.6);
      const altitude = 36 + ctx.rng.range(0, 6);

      b.spawnRaw(fodderMarineCard, team, posX, posY, {
        isParachuting: true,
        parachuteTimer: duration,
        parachuteDuration: duration,
        parachuteAltitude: altitude
      });
    }

    if (ctx.audio?.playSpellFodder) {
      ctx.audio.playSpellFodder({ x: clampedX });
    } else {
      ctx.audio?.playBark?.('Human', 'spawn', { x: clampedX });
    }
  },

  forced_overstimulation: (ctx) => {
    if (ctx.audio?.playForcedOverstimulation) {
      ctx.audio.playForcedOverstimulation();
    } else {
      ctx.audio?.playSpellBuff?.();
    }
    const b = ctx.battlefield;
    if (!b || !b.units) return;
    const base = b.bases?.[ctx.team];
    if (base && ctx.particles?.addShockwave) {
      ctx.particles.addShockwave(base.x, base.y, 350, '#ef4444', 0.5);
    }
    for (let i = 0; i < b.units.length; i++) {
      const u = b.units[i];
      if (u.team === ctx.team && !u.isDead && !u.isStructure) {
        u.applyForcedOverstimulation(b, ctx.particles, ctx.audio);
      }
    }
  },

  experimental_metamorphosis: (ctx) => {
    if (ctx.audio?.playExperimentalMetamorphosis) {
      ctx.audio.playExperimentalMetamorphosis();
    } else {
      ctx.audio?.playSpellBuff?.();
    }
    const b = ctx.battlefield;
    if (!b || !b.units) return;

    // Trigger map-wide atmospheric Sul fog event
    b.triggerMetamorphosisFogEvent?.(ctx.team);

    // Verpuppe alle lebenden mobilen Einheiten beider Teams (Strukturen & Goldstatuen sind immun)
    for (let i = 0; i < b.units.length; i++) {
      const u = b.units[i];
      if (u.isDead || u.isStructure || u.isGoldenStatue || u.isCinematic) continue;
      u.applyCocoon(b, ctx.particles, ctx.audio);
    }
    for (let i = 0; i < b.units.length; i++) {
      if (!b.units[i].isCocoon) {
        b.units[i].target = null;
      }
    }
  },

  precision_strike: (ctx) => {
    ctx.battlefield?.triggerPrecisionStrike?.(ctx.team);
  },

  retreat_protocol: (ctx) => {
    ctx.battlefield?.triggerRetreatProtocol?.(ctx.team);
  },

  ambush: (ctx) => {
    const cs = ctx.cardSystem;
    const b = ctx.battlefield;
    const team = ctx.team;
    const hand = cs?.hands?.[team];
    if (!hand || !b) return;

    // Collect all Unit cards (including Hyper/Melt units) currently in hand and empty their slots
    const unitsToSpawn = [];
    for (let s = 0; s < cs.maxHandSize; s++) {
      const c = hand[s];
      if (c && !c.pending && !c.hidden && (c.type === 'Unit' || c.type === 'Melt' || c.isMelt) && !c.isSpell && !c.isStructure && !c.isModule && !c.isBuilding) {
        unitsToSpawn.push(c);
        hand[s] = null; // Emptied slot stays empty until a card is drawn
      }
    }

    if (unitsToSpawn.length === 0) return;

    // Frontline X-coordinate at bonus indicator / territory divider
    const dividerX = (b.territoryDivider !== undefined && b.territoryDivider !== null)
      ? b.territoryDivider
      : (b.width / 2);
    const targetX = team === 'western' ? (dividerX - 25) : (dividerX + 25);
    const base = b.bases[team];
    const clampedX = team === 'western'
      ? Math.max(base.x + 130, Math.min(targetX, b.width - 160))
      : Math.min(base.x - 130, Math.max(targetX, 160));

    // Vertical alignment as a closed row across the lane center
    const laneCenterY = b.laneY + (b.laneHeight / 2);
    const totalCount = unitsToSpawn.length;
    const rowSpacing = 38;
    const totalRowHeight = (totalCount - 1) * rowSpacing;
    const startY = laneCenterY - (totalRowHeight / 2);

    const isLocal = team === (cs?.game?.myRole || cs?.game?.myTeam || 'western');

    unitsToSpawn.forEach((cardData, idx) => {
      const posY = Math.max(b.laneY + 35, Math.min(b.laneY + b.laneHeight - 35, startY + (idx * rowSpacing)));

      b.spawnRaw(cardData, team, clampedX, posY, {
        ...(cardData.edition ? { edition: cardData.edition } : {}),
        ...(cardData.customAuraHex ? { customAuraHex: cardData.customAuraHex } : {})
      });

      b.stats?.recordCardPlayed(team, cardData.race);
      if (cardData.isFlyer || cardData.isAir || (cardData.flags && cardData.flags.includes('Flyer'))) {
        b.stats?.recordAirPlayed(team);
      }

      if (isLocal) {
        if (cardData.name === 'The Salvage Company' || cardData.unitType === 'TheSalvageCompany') {
          ctx.audio?.playSalvageHorn?.();
        } else if (!hasCustomSpawnSound(cardData)) {
          ctx.audio?.playUnitSpawn?.(cardData);
        }
      }
    });

    if (isLocal) {
      ctx.audio?.playBark?.('Sul', 'spawn', { x: clampedX });
    }

    cs.checkDrilling(team);
  },

  // Unknown spellEffect string - keeps a malformed/future card from being a
  // total no-op while it's being worked on.
  _fallback: (ctx) => { ctx.battlefield.spawnUnitByName('Marine', ctx.team); }
};

export class CardSystem {
  constructor(economy, battlefield, particles, audio, rng = null) {
    this.economy = economy;
    this.battlefield = battlefield;
    if (this.battlefield) {
      if (!this.battlefield.economy) {
        this.battlefield.economy = economy;
      }
      if (economy && !economy.battlefield) {
        economy.battlefield = battlefield;
      }
    }
    this.particles = particles;
    this.audio = audio;
    // Local single-player RNG (js/engine/rng.js). Unused for either team in
    // an online match - see drawSources below and MULTIPLAYER_PLAN.md
    // section 7 for why draws are server-issued there instead.
    this.rng = rng || UnseededRNG;

    // Draw sources, keyed by team. null (the default, and always the case
    // on the authoritative server - server/gameSimulation.js never calls
    // setDrawSource()) means "draw synchronously via this.rng" below. A
    // { pop: () => {cardName} | null } source instead resolves a draw from
    // an external buffer, one card at a time - see drawCard()/
    // _resolvePendingDraw() and PENDING_CARD's comment.
    this.drawSources = { western: null, eastern: null };

    this.maxHandSize = BALANCE.maxHandSize;
    this.drawCost = BALANCE.drawCost;

    this.playerDeck = [];
    this.enemyDeck = [];

    this.hands = {
      western: new Array(this.maxHandSize).fill(null),
      eastern: new Array(this.maxHandSize).fill(null)
    };

    this.discounts = {
      western: { multiplier: 1.0, timer: 0 },
      eastern: { multiplier: 1.0, timer: 0 }
    };

    // Drilling: a detected triplet waits DRILLING_MELD_DELAY seconds before
    // actually fusing - see checkDrilling()/_resolvePendingMelt() below.
    this.pendingMelts = { western: null, eastern: null };

    // Flat draw rates override (null = defer to BALANCE.flatDrawRates; boolean = explicit instance override)
    this.flatDraw = null;

    this.mentalExtractionSeq = 0;
    this.lastMentalExtractionSlot = null;
  }

  static computeDeckProfile(resolvedDeck) {
    let heavyCount = 0;
    let maxCost = 0;
    if (Array.isArray(resolvedDeck)) {
      for (let i = 0; i < resolvedDeck.length; i++) {
        const c = resolvedDeck[i];
        if (!c) continue;
        const cost = c.cost !== undefined ? c.cost : (c.plasmaCost || 0);
        if (cost >= 40) heavyCount++;
        if (cost > maxCost) maxCost = cost;
      }
    }
    return { heavyCount, maxCost };
  }

  getDeckProfile(team = 'western') {
    const deck = (this.is2v2 && this.decks?.[team])
      ? this.decks[team]
      : (team === 'western' ? this.playerDeck : this.enemyDeck);
    return CardSystem.computeDeckProfile(deck);
  }

  init2v2({ decks = {}, races = {} } = {}) {
    this.is2v2 = true;
    this.decks = {
      w1: resolveDeckCards(decks.w1 || 'Standard'),
      w2: resolveDeckCards(decks.w2 || 'Standard'),
      e1: resolveDeckCards(decks.e1 || 'Standard'),
      e2: resolveDeckCards(decks.e2 || 'Standard')
    };
    this.playerDeck = this.decks.w1;
    this.enemyDeck = this.decks.e1;

    const westProfile = CardSystem.computeDeckProfile(this.decks.w1);
    const eastProfile = CardSystem.computeDeckProfile(this.decks.e1);
    this.battlefield?.stats?.recordDeckProfile('western', westProfile.heavyCount, westProfile.maxCost);
    this.battlefield?.stats?.recordDeckProfile('eastern', eastProfile.heavyCount, eastProfile.maxCost);

    this.hands.w1 = new Array(this.maxHandSize).fill(null);
    this.hands.w2 = new Array(this.maxHandSize).fill(null);
    this.hands.e1 = new Array(this.maxHandSize).fill(null);
    this.hands.e2 = new Array(this.maxHandSize).fill(null);

    // Keep aliases for backward compatibility with code expecting western/eastern
    this.hands.western = this.hands.w1;
    this.hands.eastern = this.hands.e1;

    this.discounts.w1 = { multiplier: 1.0, timer: 0 };
    this.discounts.w2 = { multiplier: 1.0, timer: 0 };
    this.discounts.e1 = { multiplier: 1.0, timer: 0 };
    this.discounts.e2 = { multiplier: 1.0, timer: 0 };

    this.pendingMelts.w1 = null;
    this.pendingMelts.w2 = null;
    this.pendingMelts.e1 = null;
    this.pendingMelts.e2 = null;

    this.drawSources.w1 = null;
    this.drawSources.w2 = null;
    this.drawSources.e1 = null;
    this.drawSources.e2 = null;

    this.teamRaces = this.teamRaces || { western: 'Human', eastern: 'Human' };
    if (races.w1) this.teamRaces.w1 = races.w1;
    if (races.w2) this.teamRaces.w2 = races.w2;
    if (races.e1) this.teamRaces.e1 = races.e1;
    if (races.e2) this.teamRaces.e2 = races.e2;
  }

  nextInstanceId() {
    return 'card_' + (cardInstanceCounter++);
  }

  getOpponentHands(team) {
    if (this.is2v2) {
      if (team === 'western' || team === 'w1' || team === 'w2') {
        return [this.hands.e1, this.hands.e2].filter(Boolean);
      }
      return [this.hands.w1, this.hands.w2].filter(Boolean);
    }
    if (this.game?.getOpponentTeams) {
      return this.game.getOpponentTeams(team).map(t => this.hands[t]).filter(Boolean);
    }
    const opp = (team === 'western' || team === 'w1' || team === 'w2') ? 'eastern' : 'western';
    return this.hands[opp] ? [this.hands[opp]] : [];
  }

  setDeck(team, deckIdOrCustomList) {
    if (team === 'western') {
      this.playerDeckId = typeof deckIdOrCustomList === 'string' ? deckIdOrCustomList : null;
    }

    const resolvedDeck = resolveDeckCards(deckIdOrCustomList);
    if (team === 'western') {
      this.playerDeck = resolvedDeck;
    } else {
      this.enemyDeck = resolvedDeck;
    }

    const race = deriveBaseRace(deckIdOrCustomList, resolvedDeck);
    this.teamRaces = this.teamRaces || { western: 'Human', eastern: 'Human' };
    this.teamRaces[team] = race;
    if (this.battlefield?.bases?.[team]) {
      this.battlefield.bases[team].race = race;
    }

    const { heavyCount, maxCost } = CardSystem.computeDeckProfile(resolvedDeck);
    this.battlefield?.stats?.recordDeckProfile(team, heavyCount, maxCost);

    this.hands[team] = new Array(this.maxHandSize).fill(null);
    // A pending melt belongs to the hand it was detected in. Leaving it
    // behind (e.g. from the start hand init() deals at page load, before
    // any match ticks update()) would freeze those slots forever in an
    // online match, where the client never ticks update() at all.
    this.pendingMelts[team] = null;
  }

  // Called once per frame. Drawing itself no longer happens here - it's
  // player-initiated via the DRAW command (see CommandProcessor._draw()) -
  // this only ticks the (engine-retained, currently unreachable since no
  // discount spell is enabled) discount timer and resolves any of MY OWN
  // hand slots still waiting on a card identity from the network buffer.
  update(dt) {
    const keys = this.is2v2 ? ['w1', 'w2', 'e1', 'e2'] : ['western', 'eastern'];
    for (const team of keys) {
      if (this.discounts[team] && this.discounts[team].timer > 0) {
        this.discounts[team].timer -= dt * 1000;
        if (this.discounts[team].timer <= 0) {
          this.discounts[team].multiplier = 1.0;
        }
      }

      const pendingMelt = this.pendingMelts[team];
      if (pendingMelt) {
        pendingMelt.timer -= dt;
        if (pendingMelt.timer <= 0) {
          this._resolvePendingMelt(team, pendingMelt);
        }
      }

      const source = this.drawSources[team];
      if (!source || source.hidden) continue; // no pending-resolution needed
      const hand = this.hands[team];
      if (!hand) continue;
      for (let slot = 0; slot < hand.length; slot++) {
        if (hand[slot]?.pending) {
          this._resolvePendingDraw(team, slot, source);
        }
      }
    }
  }

  _resolvePendingDraw(team, slot, source) {
    let found = null;
    let attempts = 0;
    while (attempts < 50) {
      attempts++;
      const next = source.pop();
      if (!next) return; // buffer still hasn't caught up - retry next tick
      const cardData = getCardByName(next.cardName);
      if (!cardData) return;
      if (this.isStructureAlreadyOnField(team, cardData) || this.isStructureInHand(team, cardData)) {
        continue;
      }
      if (this.isModuleUnavailable(team, cardData)) {
        continue;
      }
      if (this.isHeavyDuplicateLocked(team, cardData)) {
        continue;
      }
      found = cardData;
      break;
    }
    if (!found) return;
    this.hands[team][slot] = {
      ...found,
      slot,
      instanceId: this.hands[team][slot].instanceId
    };
    if (team === (this.game?.myRole || this.game?.myTeam || 'western')) this.audio?.playCardDraw?.();
    this.checkDrilling(team);
  }

  setDrawSource(team, source) {
    this.drawSources[team] = source;
  }

  // Resolves ONE hand slot's card identity. The caller (CommandProcessor
  // ._draw()) has already deterministically claimed the slot and paid the
  // plasma cost before this runs - both of those happen identically on
  // every client regardless of network conditions. Only identity
  // resolution below can legitimately differ in *timing* between clients
  // (an online buffer underrun), which is why it degrades to PENDING_CARD
  // instead of leaving the slot untouched - see PENDING_CARD's comment.
  drawCard(team, slotIndex, options = {}) {
    let deck;
    if (this.is2v2 && this.decks && this.decks[team]) {
      deck = this.decks[team];
    } else {
      deck = (team === 'western' || team === 'w1' || team === 'w2') ? this.playerDeck : this.enemyDeck;
    }
    if (!deck || deck.length === 0) return false;

    const source = this.drawSources[team];
    let drawnCard;
    const hand = this.hands[team];
    if (!hand) return false;

    if (source?.hidden) {
      // Online match, opponent's team: identity stays unknown client-side
      // until they actually play it - rolling a real card here would let a
      // modified client read their hand. See HIDDEN_CARD above.
      drawnCard = HIDDEN_CARD;
    } else if (source) {
      // A buffered (non-null, non-hidden) draw source: card identity comes
      // one at a time from an external buffer instead of this.rng - see
      // this constructor's drawSources comment above.
      let found = null;
      let attempts = 0;
      while (attempts < 50) {
        attempts++;
        const next = source.pop();
        if (!next) {
          // Buffer underrun - the slot is still claimed and paid for, just
          // with an unresolved identity. update() retries every tick.
          hand[slotIndex] = { ...PENDING_CARD, slot: slotIndex, instanceId: 'card_' + (cardInstanceCounter++) };
          return true;
        }
        const cardData = getCardByName(next.cardName);
        if (!cardData) return false;

        // Structure rule: while a team already has this structure on the field OR in hand,
        // skip this duplicate draw so a player never holds more than one copy in hand.
        if (this.isStructureAlreadyOnField(team, cardData) || this.isStructureInHand(team, cardData)) {
          continue;
        }
        // Module rule: permanently spent once played - see isModuleUnavailable().
        if (this.isModuleUnavailable(team, cardData)) {
          continue;
        }
        // Heavy-Card Anti-Lockout (docs/INVARIANTS.md §19): max 3 copies in hand if cost >= 40.
        if (this.isHeavyDuplicateLocked(team, cardData)) {
          continue;
        }
        if (options.excludeRarity && (cardData.rarity === options.excludeRarity || (options.excludeRarity === 'Epic' && (cardData.isSpell || cardData.type === 'Spell')))) {
          continue;
        }
        found = cardData;
        break;
      }
      if (!found) return false;
      drawnCard = found;
    } else {
      // Single-player: draw locally via this.rng. Two-Stage Tiered Draw
      // weighted by BALANCE.rarityWeights, normalized dynamically over
      // available card pools. A Structure already alive on the field OR
      // already sitting unplayed in hand is excluded from the pool - see
      // isStructureAlreadyOnField()/isStructureInHand() below - falling back
      // to the full deck only if that would otherwise empty the pool entirely.
      let available = deck.filter(c => (
        !this.isStructureAlreadyOnField(team, c) &&
        !this.isStructureInHand(team, c) &&
        !this.isModuleUnavailable(team, c) &&
        !this.isHeavyDuplicateLocked(team, c)
      ));
      if (options.excludeRarity) {
        const filtered = available.filter(c => c.rarity !== options.excludeRarity && (!c.isSpell || options.excludeRarity !== 'Epic'));
        if (filtered.length > 0) available = filtered;
      }
      let pool = available;
      if (pool.length === 0) {
        const safeFallback = deck.filter(c => !this.isModuleUnavailable(team, c) && !this.isStructureAlreadyOnField(team, c));
        pool = safeFallback.length > 0 ? safeFallback : deck;
      }
      const useFlat = (this.flatDraw !== null && this.flatDraw !== undefined)
        ? !!this.flatDraw
        : !!BALANCE.flatDrawRates;
      drawnCard = useFlat
        ? this._pickCardForTeam(team, pool)
        : pickWeightedCard(pool, BALANCE.rarityWeights, this.rng);
    }

    hand[slotIndex] = {
      ...drawnCard,
      slot: slotIndex,
      instanceId: 'card_' + (cardInstanceCounter++)
    };

    if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
      this.audio?.playCardDraw?.();
    }
    this.checkDrilling(team);
    return true;
  }

  // Resolves flat-draw card selection, routing AI bots to dynamic situational weighting
  _pickCardForTeam(team, pool) {
    if (!pool || pool.length === 0) return null;
    const isBot = (team === 'eastern' && (this.game?.ai || this.aiEastern || this.ai)) ||
      (team === 'western' && (this.game?.aiWestern || this.aiWestern)) ||
      (this.game?.isExhibition && team === 'eastern');
    if (isBot) {
      return this._pickDynamicBotCard(team, pool);
    }
    return this.rng.pick(pool);
  }

  // Subtle Situational & Fusion-Magnetism weighting for AI bot draws
  _pickDynamicBotCard(team, pool) {
    if (!pool || pool.length === 0) return null;
    if (pool.length === 1) return pool[0];

    const bot = (team === 'eastern')
      ? (this.game?.ai || this.aiEastern || this.ai)
      : (this.game?.aiWestern || this.aiWestern);
    const difficulty = bot?.difficulty || this.game?.currentAIDifficulty || 'medium';

    // Easy bots do not use dynamic draw bias
    if (difficulty === 'easy') {
      return this.rng.pick(pool);
    }

    // Scaling factor for subtle bias across tiers:
    // medium: 0.8, hard: 1.0, veryHard: 1.4, insane: 1.8, invincible: 2.2
    const biasScale = (difficulty === 'medium') ? 0.8
      : (difficulty === 'hard') ? 1.0
      : (difficulty === 'veryHard') ? 1.4
      : (difficulty === 'insane') ? 1.8
      : 2.2;

    const hand = this.hands[team] || [];
    const oppTeam = (team === 'western' || team === 'w1' || team === 'w2') ? 'eastern' : 'western';
    const units = this.battlefield?.units || [];

    // 1. Air Threat Check
    const enemyHasAir = Boolean(units.some(u => u.team === oppTeam && !u.isDead && u.isAir));
    const botHasAntiAirInHand = hand.some(c => c && c.targetsAir !== false && ((c.damage || 0) > 0 || c.hasDualTether || c.hasBeamTether));

    // 2. Heavy Hand Check
    const heavyInHandCount = hand.filter(c => c && (c.cost || 0) >= 25).length;

    // 3. Fusion Magnetism Check (pairs in hand + Pulp wildcard pairs)
    const cardCountsInHand = {};
    const fusibleInHand = new Set();
    for (const c of hand) {
      if (c && c.name && c.type !== 'Melt') {
        cardCountsInHand[c.name] = (cardCountsInHand[c.name] || 0) + 1;
        if (c.name !== 'Pulp' && this.getFusionInfo(c, team)) {
          fusibleInHand.add(c.name);
        }
      }
    }
    const pairNames = new Set(
      Object.entries(cardCountsInHand)
        .filter(([name, count]) => count === 2 && fusibleInHand.has(name))
        .map(([name]) => name)
    );
    const pulpInHandCount = cardCountsInHand['Pulp'] || 0;
    const wildcardPairNames = new Set();
    if (pulpInHandCount >= 1) {
      for (const fusibleName of fusibleInHand) {
        wildcardPairNames.add(fusibleName);
      }
    }
    const hasFusiblePairForPulp = Array.from(pairNames).some(n => fusibleInHand.has(n));

    // 4. Live Board Threat Checks (Siege, Stealth, Swarm, Heavy Armor, Glass Cannons)
    let enemyHasSiege = false;
    let enemyHasStealth = Boolean(
      bot?.opponentProfile?.hasStealth ||
      bot?.liveOpponentProfile?.hasStealth ||
      (bot?.enemyMemory && (bot.gameTime - (bot.enemyMemory.recentCloakTime ?? -999)) < 30)
    );
    let enemySwarmCount = 0;
    let enemyHasHeavyArmor = false;
    let enemyHasGlassCannon = false;
    let enemyHasHacker = Boolean(
      bot?.opponentProfile?.hasHacker ||
      bot?.liveOpponentProfile?.hasHacker ||
      (bot?.enemyMemory && (bot.gameTime - (bot.enemyMemory.recentHackerTime ?? -999)) < 25)
    );
    let friendlyDisabledByHacker = false;

    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      if (u.isDead) continue;
      if (u.team === team && u.isDisabled && (u.disabledByHackerId || u.isHacked)) {
        friendlyDisabledByHacker = true;
        continue;
      }
      if (u.team !== oppTeam) continue;
      if (u.isHacker && !u.isTranscendent && u.hackerState !== 'EXHAUSTED') {
        enemyHasHacker = true;
      }
      if (u.isCloaked || u.isAntiShade || u.name === 'Shade') {
        enemyHasStealth = true;
      }
      if (u.isStructure || (u.range || 0) >= 15) {
        enemyHasSiege = true;
      }
      if (!u.isAir && !u.isStructure && ((u.cardData?.cost || 10) <= 14 || u.attributes?.includes('Light'))) {
        enemySwarmCount++;
      }
      if ((u.hp || 0) >= 150 || (u.deflectionRating || 0) >= 0.20 || u.attributes?.includes('Mechanical') || u.attributes?.includes('Massive')) {
        enemyHasHeavyArmor = true;
      }
      const dps = (u.damage || 0) / Math.max(0.25, u.attackSpeed || 1);
      if (((u.damage || 0) >= 50 || dps >= 20) && (u.hp || 10) <= 60 && !u.isStructure) {
        enemyHasGlassCannon = true;
      }
    }

    const teamHasDetection = Boolean(this.battlefield?.hasTeamDetection?.(team));
    const botHasDetectorInHand = hand.some(c => c && (c.isDetector || c.detectsCloak || c.name === 'A9 Grizzly' || c.name === 'Delving Pillar' || c.moduleId === 'intel_uplink'));
    const botHasSplashInHand = hand.some(c => c && ((c.splashRadius || 0) > 0 || (c.capsuleSplashRadius || 0) > 0 || c.antiMarineBonus));
    const botHasArmorBreakerInHand = hand.some(c => c && (c.projectileType === 'Energy' || c.deflectionScaling || (c.damage || 0) >= 35));
    const botHasHackerInHand = hand.some(c => c && (c.name === 'Hacker' || c.unitType === 'Hacker'));

    // Calculate dynamic weights per card in pool
    const weights = [];
    let totalWeight = 0;

    for (const card of pool) {
      let w = 1.0;
      const cost = card.cost || 0;
      const armed = (card.damage || 0) > 0 || card.hasDualTether || card.hasBeamTether;
      const isAntiAir = armed && card.targetsAir !== false;

      // A. Anti-Air boost when enemy has air and bot has none in hand
      if (enemyHasAir && !botHasAntiAirInHand && isAntiAir) {
        w += (1.5 * biasScale);
      }

      // B. Fusion Magnetism: if holding 2 of this card (or 1 + Pulp wildcard), attract the 3rd for Hyper Drilling / Symbiosis
      if (pairNames.has(card.name)) {
        w += (4.0 * biasScale);
      } else if (wildcardPairNames.has(card.name)) {
        w += (3.0 * biasScale);
      } else if (card.name === 'Pulp') {
        if (hasFusiblePairForPulp) {
          w += (3.5 * biasScale);
        } else if (pulpInHandCount === 1 && fusibleInHand.size > 0) {
          w += (2.5 * biasScale);
        }
      }

      // C. Dead Hand prevention: if bot already holds 2+ heavy cards, damp heavy cards
      if (heavyInHandCount >= 2) {
        if (cost >= 25) {
          w *= 0.45;
        } else if (cost <= 12 && !card.isSpell) {
          w += (0.8 * biasScale);
        }
      }

      // D. Anti-Siege boost if enemy has siege
      if (enemyHasSiege && (card.name === 'Rocketship' || card.name === 'Precision Strike' || card.name === 'A9 Piranha' || card.isAir)) {
        w += (0.9 * biasScale);
      }

      // E. Anti-Stealth boost if enemy has cloaked threats and bot lacks detection
      if (enemyHasStealth && !teamHasDetection && !botHasDetectorInHand) {
        const isDetector = card.isDetector || card.detectsCloak || card.name === 'A9 Grizzly' || card.name === 'Delving Pillar' || card.moduleId === 'intel_uplink';
        if (isDetector) {
          w += (2.2 * biasScale);
        } else if (card.name === 'Anti Shade' || (card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0) {
          w += (1.1 * biasScale);
        }
      }

      // F. Anti-Swarm boost if enemy floods 3+ light ground units
      if (enemySwarmCount >= 3 && !botHasSplashInHand) {
        if ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0 || card.antiMarineBonus) {
          w += (1.4 * biasScale);
        }
      }

      // G. Anti-Armor & Anti-Glass-Cannon boost
      if (enemyHasHeavyArmor && !botHasArmorBreakerInHand) {
        if (card.projectileType === 'Energy' || card.deflectionScaling || (card.damage || 0) >= 35) {
          w += (1.3 * biasScale);
        }
      }
      if (enemyHasGlassCannon) {
        if (card.spellEffect === 'precision_strike' || card.name === 'Precision Strike' || (card.splashRadius || 0) > 0 || (card.speed || 0) >= 3.0) {
          w += (1.5 * biasScale);
        }
      }

      // H. Anti-Hacker boost: enemy has deployed Hacker or disabled ally, and bot holds no Hacker in hand
      if ((enemyHasHacker || friendlyDisabledByHacker) && !botHasHackerInHand && (card.name === 'Hacker' || card.unitType === 'Hacker')) {
        w += (friendlyDisabledByHacker ? 3.5 : 2.5) * biasScale;
      }

      weights.push(w);
      totalWeight += w;
    }

    if (totalWeight <= 0) return this.rng.pick(pool);

    let roll = this.rng.next() * totalWeight;
    for (let i = 0; i < pool.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return pool[i];
    }
    return pool[pool.length - 1];
  }

  // Internal helper to simulate picking 1 card for candidate hand evaluation
  _pickOneFromDeck(team, candidateHandCards = [], drawOpts = {}) {
    let deck;
    if (this.is2v2 && this.decks && this.decks[team]) {
      deck = this.decks[team];
    } else {
      deck = (team === 'western' || team === 'w1' || team === 'w2') ? this.playerDeck : this.enemyDeck;
    }
    if (!deck || deck.length === 0) return null;

    const available = deck.filter(c => {
      if (this.isStructureAlreadyOnField(team, c)) return false;
      if ((c.isStructure || c.type === 'Structure') && candidateHandCards.some(hc => hc?.name === c.name)) return false;
      if ((c.isModule || c.type === 'Module') && candidateHandCards.some(hc => hc && (hc.name === c.name || (c.moduleId && hc.moduleId === c.moduleId)))) return false;
      if (this.isModuleUnavailable(team, c)) return false;
      if ((c.name === 'Anti Shade' || (c.cannotAttackBuildings && (c.cost || 0) >= 40)) && candidateHandCards.filter(hc => hc?.name === c.name).length >= 1) return false;
      if ((c.cost || 0) >= (BALANCE.heavyCardLockCostThreshold || 40)) {
        const heavyCount = candidateHandCards.filter(hc => (hc?.cost || 0) >= (BALANCE.heavyCardLockCostThreshold || 40)).length;
        if (heavyCount >= (BALANCE.heavyCardLockMaxCopies || 3)) return false;
      }
      return true;
    });

    let pool = available;
    if (drawOpts.excludeRarity) {
      const filtered = pool.filter(c => c.rarity !== drawOpts.excludeRarity && (!c.isSpell || drawOpts.excludeRarity !== 'Epic'));
      if (filtered.length > 0) pool = filtered;
    }
    if (pool.length === 0) {
      const safeFallback = deck.filter(c => !this.isModuleUnavailable(team, c) && !this.isStructureAlreadyOnField(team, c));
      pool = safeFallback.length > 0 ? safeFallback : deck;
    }

    const useFlat = (this.flatDraw !== null && this.flatDraw !== undefined)
      ? !!this.flatDraw
      : !!BALANCE.flatDrawRates;

    return useFlat
      ? this.rng.pick(pool)
      : pickWeightedCard(pool, BALANCE.rarityWeights, this.rng);
  }

  // Rolls one 5-card candidate hand for starting hand smoothing
  _rollCandidateHand(team, count = 5, options = {}) {
    const candidate = [];
    for (let slot = 0; slot < count; slot++) {
      const allowEpic = (options.allowEpicInSlot === undefined || options.allowEpicInSlot === null)
        ? true
        : (slot === options.allowEpicInSlot);
      const drawOpts = allowEpic ? {} : { excludeRarity: 'Epic' };
      const card = this._pickOneFromDeck(team, candidate, drawOpts);
      if (card) candidate.push(card);
    }
    return candidate;
  }

  // Evaluates opening hand early-curve viability (Anti-Brick scoring)
  _evaluateStartingHandScore(cards) {
    if (!cards || cards.length === 0) return -999;
    let score = 0;
    let cheapCount = 0;
    let heavyCount = 0;
    let midCount = 0;
    const nameCounts = {};

    for (const card of cards) {
      if (!card) continue;
      const cost = card.cost || 0;
      nameCounts[card.name] = (nameCounts[card.name] || 0) + 1;

      if (cost <= 12 && !card.isSpell && card.type !== 'Spell') {
        cheapCount++;
      } else if (cost >= 28) {
        heavyCount++;
      } else {
        midCount++;
      }

      // Situational cards penalty in opening hand
      if (card.name === 'Claiming Pillar' || card.name === 'Experimental Metamorphosis' || card.name === 'Anti Shade') {
        score -= 20;
      }
    }

    // Reward healthy early-game curve
    if (cheapCount === 0) {
      score -= 120; // Severe brick penalty
    } else if (cheapCount === 1) {
      score += 35;
    } else if (cheapCount === 2) {
      score += 60; // Sweet spot: 2 cheap cards
    } else if (cheapCount === 3) {
      score += 45;
    } else {
      score += 20;
    }

    if (midCount >= 1 && midCount <= 2) {
      score += 20; // Mid-game progression
    }

    // Heavy card penalty
    if (heavyCount === 1) {
      score += 5; // 1 heavy card is a fine late-game goal
    } else if (heavyCount === 2) {
      score -= 15;
    } else if (heavyCount >= 3) {
      score -= (heavyCount * 30); // 3+ heavy cards heavily penalized
    }

    // Reward pairs / triplets for drilling potential
    for (const count of Object.values(nameCounts)) {
      if (count === 2) score += 15;
      if (count >= 3) score += 40;
    }

    return score;
  }

  // Batch Draw (Starthand & Concentration Reload) with Soft Anti-Cascade & Opening Hand Smoothing (Variante A):
  // Slot 0 (the joker slot) draws from the full deck including Epic (~3% chance).
  // Slots 1..count-1 draw from the non-epic pool (Common, Uncommon, Rare).
  // Evaluates two 5-card candidate hands in background (MTG Arena virtual reroll)
  // to prevent dead hands in both Singleplayer and authoritative Multiplayer.
  drawBatch(team, count = this.maxHandSize, options = { allowEpicInSlot: 0 }) {
    const source = this.drawSources[team];
    if (source) {
      for (let slot = 0; slot < count; slot++) {
        const allowEpic = (options.allowEpicInSlot === undefined || options.allowEpicInSlot === null)
          ? true
          : (slot === options.allowEpicInSlot);
        const drawOpts = allowEpic ? {} : { excludeRarity: 'Epic' };
        this.drawCard(team, slot, drawOpts);
      }
      return;
    }

    // Opening Hand Smoothing (Variante A - Virtual Reroll):
    if (count === this.maxHandSize && (!options || options.smoothHand !== false)) {
      const h1 = this._rollCandidateHand(team, count, options);
      const h2 = this._rollCandidateHand(team, count, options);
      const s1 = this._evaluateStartingHandScore(h1);
      const s2 = this._evaluateStartingHandScore(h2);
      const chosen = (s2 > s1) ? h2 : h1;

      const hand = this.hands[team];
      if (hand && chosen.length === count) {
        for (let slot = 0; slot < count; slot++) {
          hand[slot] = {
            ...chosen[slot],
            slot,
            instanceId: 'card_' + (cardInstanceCounter++)
          };
        }
        if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
          this.audio?.playCardDraw?.();
        }
        this.checkDrilling(team);
        return;
      }
    }

    // Standard fallback batch draw
    for (let slot = 0; slot < count; slot++) {
      const allowEpic = (options.allowEpicInSlot === undefined || options.allowEpicInSlot === null)
        ? true
        : (slot === options.allowEpicInSlot);
      const drawOpts = allowEpic ? {} : { excludeRarity: 'Epic' };
      this.drawCard(team, slot, drawOpts);
    }
  }

  // "Drilling": 3 identical cards ANYWHERE in the hand (not required to be
  // adjacent) fuse into the single "Hyper <name>" card (found by name, must
  // carry type:"Melt" - see js/data/cards.js) in the median-indexed of the
  // 3 slots; the other two empty out exactly as if their cards had been
  // played, ready to be drawn into again. Called after every hand slot
  // resolves a real card identity (drawCard()/_resolvePendingDraw() below)
  // - never after playCard(), which can only ever empty a slot, not create
  // a fresh run of 3 matches.
  //
  // Doesn't fuse immediately: a fresh triplet is only scheduled here (see
  // pendingMelts/DRILLING_MELD_DELAY above) so the 3 matching cards actually
  // sit visible together for a moment before vanishing into the merge -
  // _resolvePendingMelt() below does the actual fusing once that delay
  // elapses. Already having a pending melt for this team is left alone
  // rather than re-scanning - it re-validates itself when it resolves, and
  // a 5-slot hand can only ever hold one fresh triple of a single name at a
  // time anyway (3+3 of two different names would need 6 slots).
  //
  // Guards against HIDDEN_CARD/PENDING_CARD: an opponent's hand is only ever
  // visible to me as repeated HIDDEN_CARD placeholders (see HIDDEN_CARD's
  // comment above) which would otherwise look like 3 "identical" cards to
  // this scanner. Ignored here so an opponent drawing 3 times in an online
  // match doesn't hallucinate a fake melt in my local view of their hand.
  checkDrilling(team) {
    // Online, only the server melts - the client never ticks update(), so a
    // melt scheduled here would never resolve and would lock its slots.
    if (this.game?.isOnline) return;
    if (this.pendingMelts[team]) return; // one at a time, let it resolve or fizzle

    const hand = this.hands[team];
    if (!hand) return;
    const matchingSlotsByName = new Map();
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (!c || c.pending || c.hidden || c.type === 'Melt' || c.race === 'Sul') continue;
      const arr = matchingSlotsByName.get(c.name) || [];
      arr.push(i);
      matchingSlotsByName.set(c.name, arr);
    }

    for (const [name, slots] of matchingSlotsByName.entries()) {
      if (slots.length < 3) continue;

      const meltCard = getCardByName(`hyper ${name.toLowerCase()}`);
      if (!meltCard || meltCard.type !== 'Melt') continue;

      const i1 = slots[0], i2 = slots[1], i3 = slots[2];
      this.pendingMelts[team] = {
        slots: [i1, i2, i3],
        resultSlot: i2,
        instanceIds: [hand[i1].instanceId, hand[i2].instanceId, hand[i3].instanceId],
        name,
        meltCard,
        timer: DRILLING_MELD_DELAY
      };
      if (team === (this.game?.myRole || this.game?.myTeam || 'western')) this.audio?.playMeltCharge?.();
      return;
    }

    // If no Human drilling was triggered, check Sul Symbiosis
    this.checkSymbiosis(team);
  }

  // "Symbiosis": Sul units fuse on 3 IDENTICAL units of the same caste
  // into a random unit of the next higher caste from the player's deck.
  // Castes: Loaf -> Pillar -> Lock -> Vulture -> Symbiosis Cards.
  checkSymbiosis(team) {
    if (this.game?.isOnline) return; // see checkDrilling()
    if (this.pendingMelts[team]) return;

    const hand = this.hands[team];
    if (!hand) return;
    const matchingSlotsByName = new Map();
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (!c || c.pending || c.hidden || c.race !== 'Sul' || !c.caste) continue;
      const arr = matchingSlotsByName.get(c.name) || [];
      arr.push(i);
      matchingSlotsByName.set(c.name, arr);
    }

    for (const [name, slots] of matchingSlotsByName.entries()) {
      if (slots.length < 3) continue;

      const sampleCard = hand[slots[0]];
      const targetCaste = NEXT_CASTE_MAP[sampleCard.caste];
      if (!targetCaste) continue;

      // Pool: drawn randomly from next-caste units present in the player's deck
      let deck;
      if (this.is2v2 && this.decks && this.decks[team]) {
        deck = this.decks[team];
      } else {
        deck = (team === 'western' || team === 'w1' || team === 'w2') ? (this.playerDeck || []) : (this.enemyDeck || []);
      }
      let candidates = deck.filter(c => c.race === 'Sul' && (c.caste === targetCaste || (targetCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))));
      if (candidates.length === 0) {
        // Fallback to active Sul cards in CARDS_DATA
        candidates = CARDS_DATA.filter(c => c.race === 'Sul' && (c.caste === targetCaste || (targetCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))));
      }

      if (candidates.length === 0) continue; // No higher tier card defined yet

      const rng = this.rng || UnseededRNG;
      const targetCard = rng.pick(candidates);
      if (!targetCard) continue;

      const [i1, i2, i3] = [slots[0], slots[1], slots[2]];
      this.pendingMelts[team] = {
        slots: [i1, i2, i3],
        resultSlot: i2,
        instanceIds: [hand[i1].instanceId, hand[i2].instanceId, hand[i3].instanceId],
        name: `${sampleCard.caste} Symbiosis`,
        meltCard: { ...targetCard },
        timer: DRILLING_MELD_DELAY,
        isSymbiosis: true
      };

      if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
        this.audio?.playMeltCharge?.();
      }
      return;
    }

    // If no Human drilling and no Sul symbiosis, check Pulp Wildcard Drilling
    this.checkPulpWildcard(team);
  }

  // "Pulp Wildcard": 1 Pulp + 2 identical valid cards, OR 2 Pulps + 1 valid card.
  // Fuses into Hyper (Human) or next caste (Sul). Doesn't care about factions.
  // 3 Pulps do not fuse (reserved for future Hyper Pulp).
  checkPulpWildcard(team) {
    if (this.game?.isOnline) return;
    if (this.pendingMelts[team]) return;

    const hand = this.hands[team];
    if (!hand) return;

    const pulpSlots = [];
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (c && !c.pending && !c.hidden && c.name === 'Pulp') {
        pulpSlots.push(i);
      }
    }

    if (pulpSlots.length === 0) return;

    // Helper: evaluates if a card has a valid fusion target
    const getFusionTarget = (card) => {
      if (!card || card.name === 'Pulp' || card.type === 'Melt' || card.isStructure || card.isSpell) return null;
      if (card.race !== 'Sul') {
        const meltCard = getCardByName(`hyper ${card.name.toLowerCase()}`);
        if (meltCard && meltCard.type === 'Melt') {
          return { kind: 'drilling', meltCard, name: card.name, isSymbiosis: false };
        }
      } else if (card.caste) {
        const targetCaste = NEXT_CASTE_MAP[card.caste];
        if (targetCaste) {
          let deck;
          if (this.is2v2 && this.decks && this.decks[team]) {
            deck = this.decks[team];
          } else {
            deck = (team === 'western' || team === 'w1' || team === 'w2') ? (this.playerDeck || []) : (this.enemyDeck || []);
          }
          let candidates = deck.filter(c => c.race === 'Sul' && (c.caste === targetCaste || (targetCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))));
          if (candidates.length === 0) {
            candidates = CARDS_DATA.filter(c => c.race === 'Sul' && (c.caste === targetCaste || (targetCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))));
          }
          if (candidates.length > 0) {
            const rng = this.rng || UnseededRNG;
            const targetCard = rng.pick(candidates);
            if (targetCard) {
              return { kind: 'symbiosis', meltCard: { ...targetCard }, name: `${card.caste} Symbiosis`, isSymbiosis: true };
            }
          }
        }
      }
      return null;
    };

    const rng = this.rng || UnseededRNG;

    // Case 1: 1+ Pulp + 2 identical valid cards in hand
    const pairsByName = new Map();
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (!c || c.pending || c.hidden || c.name === 'Pulp') continue;
      const arr = pairsByName.get(c.name) || [];
      arr.push(i);
      pairsByName.set(c.name, arr);
    }

    const candidatePairs = [];
    for (const [name, slots] of pairsByName.entries()) {
      if (slots.length >= 2) {
        const target = getFusionTarget(hand[slots[0]]);
        if (target) {
          candidatePairs.push({ slots: [slots[0], slots[1]], target });
        }
      }
    }

    if (candidatePairs.length > 0) {
      // User decision: random choice if multiple pairs exist
      const chosen = candidatePairs.length === 1 ? candidatePairs[0] : rng.pick(candidatePairs);
      const [s1, s2] = chosen.slots;
      const pSlot = pulpSlots[0];
      const i1 = s1, i2 = s2, i3 = pSlot;

      this.pendingMelts[team] = {
        slots: [i1, i2, i3],
        resultSlot: i1,
        instanceIds: [hand[i1].instanceId, hand[i2].instanceId, hand[i3].instanceId],
        name: chosen.target.name,
        meltCard: chosen.target.meltCard,
        timer: DRILLING_MELD_DELAY,
        isSymbiosis: chosen.target.isSymbiosis,
        isPulp: true,
        isWildcard: true,
        pulpSlots: [pSlot]
      };

      if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
        this.audio?.playMeltCharge?.();
      }
      return;
    }

    // Case 2: 2+ Pulps + 1 valid card in hand (random selection among valid candidates)
    if (pulpSlots.length >= 2) {
      const singleCandidates = [];
      for (let i = 0; i < hand.length; i++) {
        const c = hand[i];
        if (!c || c.pending || c.hidden || c.name === 'Pulp') continue;
        const target = getFusionTarget(c);
        if (target) {
          singleCandidates.push({ slot: i, target });
        }
      }

      if (singleCandidates.length > 0) {
        const chosen = singleCandidates.length === 1 ? singleCandidates[0] : rng.pick(singleCandidates);
        const p1 = pulpSlots[0];
        const p2 = pulpSlots[1];
        const sSlot = chosen.slot;

        this.pendingMelts[team] = {
          slots: [p1, p2, sSlot],
          resultSlot: sSlot,
          instanceIds: [hand[p1].instanceId, hand[p2].instanceId, hand[sSlot].instanceId],
          name: chosen.target.name,
          meltCard: chosen.target.meltCard,
          timer: DRILLING_MELD_DELAY,
          isSymbiosis: chosen.target.isSymbiosis,
          isPulp: true,
          isWildcard: true,
          pulpSlots: [p1, p2]
        };

        if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
          this.audio?.playMeltCharge?.();
        }
        return;
      }
    }
  }

  // Fuses a scheduled triplet once its freeze time (see checkDrilling()
  // above) has elapsed. Re-validates against the exact 3 card instances
  // originally detected (by slot + instanceId, not just slot - the 3 slots
  // are no longer necessarily adjacent) - if any of them was played, or the
  // hand otherwise changed, in the meantime, the triplet quietly fizzles
  // instead of fusing whatever happens to occupy those slots now.
  _resolvePendingMelt(team, pending) {
    this.pendingMelts[team] = null;

    const hand = this.hands[team];
    if (!hand) return;
    const [i1, i2, i3] = pending.slots;
    const a = hand[i1], b = hand[i2], c = hand[i3];
    const stillValid = a && b && c
      && a.instanceId === pending.instanceIds[0]
      && b.instanceId === pending.instanceIds[1]
      && c.instanceId === pending.instanceIds[2];

    if (stillValid) {
      for (const s of pending.slots) {
        if (s !== pending.resultSlot) hand[s] = null;
      }
      hand[pending.resultSlot] = {
        ...pending.meltCard,
        slot: pending.resultSlot,
        instanceId: 'card_' + (cardInstanceCounter++)
      };

      this.lastMeltSlot = {
        team,
        slot: pending.resultSlot,
        name: pending.name,
        meltName: pending.meltCard.name,
        isPulp: !!pending.isPulp
      };
      this.meltSeq = (this.meltSeq || 0) + 1;
      const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';
      this.battlefield?.stats?.recordMeld(effectiveTeam);
      if (team === (this.game?.myRole || this.game?.myTeam || 'western')) {
        this.audio?.playMeltFusionImpact?.();
      }

      if (this.game?.isOnline && team === (this.game?.myRole || this.game.myTeam || 'western')) {
        this.game.enqueueCommand({
          type: 'MELD',
          team,
          slots: pending.slots,
          resultSlot: pending.resultSlot,
          meltCardName: pending.meltCard.name
        });
      }
    }

    // pendingMelts[team] just freed up either way. checkDrilling() bails
    // out immediately while a melt is pending (see its own comment), so a
    // second triplet that formed elsewhere in the hand during this one's
    // DRILLING_MELD_DELAY window (e.g. one of THIS triplet's cards got
    // played, this one fizzled above, but a fresh draw completed a
    // different triplet in the meantime) was never scanned for - it would
    // otherwise sit fully matched and unfused until some later, unrelated
    // draw happened to call checkDrilling() again. Rescan right here
    // instead of leaving that window open.
    this.checkDrilling(team);
  }

  // Fusion-recipe lookup for the card zoom tooltip (js/ui/hud.js's
  // buildCardInnerHTML) - tells the player what a hand card melts into and
  // how far their current hand already is.
  // Both Human Hyper Drilling and Sul Symbiosis require 3 identical cards.
  getFusionInfo(card, team) {
    if (!card || card.pending || card.hidden) return null;
    const hand = this.hands[team];
    if (!hand) return null;

    const pulpCount = hand.filter(c => c && !c.pending && !c.hidden && c.name === 'Pulp').length;

    if (card.name === 'Pulp') {
      return {
        kind: 'wildcard',
        have: pulpCount,
        need: 3,
        description: 'Wildcard catalyst for drilling & symbiosis'
      };
    }

    if (card.race === 'Sul' && card.caste) {
      const nextCaste = NEXT_CASTE_MAP[card.caste];
      if (!nextCaste) return null;

      // Verify that next tier units exist (e.g. Vulture has no Symbiosis units in the pool yet)
      const deck = (this.is2v2 && this.decks && this.decks[team]) ? this.decks[team] : ((team === 'western' || team === 'w1' || team === 'w2') ? (this.playerDeck || []) : (this.enemyDeck || []));
      const hasCandidates = deck.some(c => c.race === 'Sul' && (c.caste === nextCaste || (nextCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))))
        || CARDS_DATA.some(c => c.race === 'Sul' && (c.caste === nextCaste || (nextCaste === 'Symbiosis' && (c.type === 'Symbiosis' || c.isSymbiosisResult))));
      if (!hasCandidates) return null;

      const have = hand.filter(c => c && !c.pending && !c.hidden && c.name === card.name).length;
      return {
        kind: 'symbiosis',
        caste: card.caste,
        nextCaste,
        have,
        pulpCount,
        need: 3
      };
    }

    if (card.race !== 'Sul' && card.type !== 'Melt') {
      const resultCard = getCardByName(`hyper ${card.name.toLowerCase()}`);
      if (resultCard && resultCard.type === 'Melt') {
        const have = hand.filter(c => c && !c.pending && !c.hidden && c.name === card.name).length;
        return { kind: 'drilling', resultName: resultCard.name, have, pulpCount, need: 3 };
      }
    }

    return null;
  }

  getEffectiveDrawCost(team) {
    const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';
    if (this.battlefield?.moduleSystem?.hasCongressBuff(effectiveTeam, 'government_contracts')) {
      return 0;
    }
    return this.drawCost ?? BALANCE.drawCost ?? 2;
  }

  getEffectiveCost(team, card) {
    if (!card) return 0;
    const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';

    if (card.spellEffect === 'ambush' || card.name === 'Ambush') {
      const hand = this.hands?.[team] || this.hands?.[effectiveTeam];
      let unitCostSum = 0;
      if (hand) {
        for (let s = 0; s < hand.length; s++) {
          const c = hand[s];
          if (c && !c.pending && !c.hidden && (c.type === 'Unit' || c.type === 'Melt' || c.isMelt) && !c.isSpell && !c.isStructure && !c.isModule && !c.isBuilding) {
            unitCostSum += (c.cost || 0);
          }
        }
      }
      const cap = card.maxCostCap !== undefined ? card.maxCostCap : 150;
      const cappedSum = Math.min(cap, unitCostSum);
      const mult = this.discounts[team]?.multiplier || this.discounts[effectiveTeam]?.multiplier || 1.0;
      return Math.max(0, Math.floor(cappedSum * mult));
    }

    const baseCost = card.cost || 0;
    const mult = this.discounts[team]?.multiplier || this.discounts[effectiveTeam]?.multiplier || 1.0;
    return Math.max(0, Math.floor(baseCost * mult));
  }

  // General Structure rule: while a team already has a living on-field
  // instance of a given Structure card, that same card can neither be drawn
  // (drawCard() above) nor played (playCard() below) again - a base can only
  // ever host one Invasion Capsule Sling, one Deployable Cannon, etc. at a
  // time. Matches by card name against battlefield units, since a card's
  // `id` is only unique within its own tier/rarity, not globally.
  isStructureAlreadyOnField(team, card) {
    if (!card || !(card.isStructure || card.type === 'Structure')) return false;
    const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';
    const units = this.battlefield?.units;
    if (!units) return false;
    return units.some(u => u.team === effectiveTeam && !u.isDead && u.isStructure && u.name === card.name);
  }

  // Same Structure-card rule, but for the DRAW pool (drawCard() above): a
  // copy already sitting unplayed in hand blocks drawing another one just
  // like a copy already built on the field does - only once that one copy
  // has actually been played away (hand slot empties, isStructureAlreadyOnField()
  // stays false until it's also destroyed) does the card become drawable
  // again. Not used by playCard()'s guard - the copy you're playing IS the
  // one in hand, so checking the hand there would be self-defeating.
  isStructureInHand(team, card) {
    if (!card || !(card.isStructure || card.type === 'Structure')) return false;
    const hand = this.hands[team];
    if (!hand) return false;
    return hand.some(c => c && !c.pending && !c.hidden && c.name === card.name);
  }

  // Module rule (docs/MODULE_SYSTEM_PLAN.md section 1.2): unlike a Structure,
  // whose draw block lifts again the moment it dies, a Module is spent for
  // the rest of the match the instant it is played - it cannot be attacked,
  // so it never comes back. This predicate therefore blocks the DRAW pool
  // permanently, covering all three cases in which drawing the card would
  // hand the player a dead card:
  //   a) the module is already built or still descending on its cable
  //   b) an unplayed copy is already sitting in hand
  //   c) the stack is full (MODULE_MAX), so nothing more can be added
  // Not used by playCard()'s own guard - that asks ModuleSystem.canBuild()
  // directly, since the copy being played IS the one in hand.
  isModuleUnavailable(team, card) {
    if (!card || !(card.isModule || card.type === 'Module')) return false;
    const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';
    const ms = this.battlefield?.moduleSystem;
    if (ms) {
      if (card.moduleId && ms.isClaimed(effectiveTeam, card.moduleId)) return true;
      if (ms.stackCount(effectiveTeam) >= MODULE_MAX) return true;
    }
    const hand = this.hands[team] || this.hands[effectiveTeam];
    if (hand) {
      if (hand.some(c => c && !c.pending && !c.hidden && (c.name === card.name || (card.moduleId && c.moduleId === card.moduleId)))) {
        return true;
      }
    }
    return false;
  }

  // Heavy-Card Anti-Lockout rule (docs/INVARIANTS.md §19):
  // When a card costs 40 or more plasma and the player already holds 3 or more
  // copies of it in hand, it is temporarily excluded from the draw pool.
  // This rule is race-neutral and naturally accommodates Melt/Drilling (which fuses
  // triplets at 3 copies into a Hyper unit, freeing hand slots) while preventing
  // high-cost non-melting units/cards from causing hand deadlocks (e.g. 5x The Salvage Company).
  // Non-melting units with strict targeting constraints (e.g. Anti Shade, cannotAttackBuildings)
  // are locked at 1 copy to protect hand functionality.
  isHeavyDuplicateLocked(team, card) {
    if (!card) return false;
    const threshold = BALANCE.heavyCardLockCostThreshold ?? 40;
    let maxCopies = BALANCE.heavyCardLockMaxCopies ?? 3;
    if (card.name === 'Anti Shade' || (card.cannotAttackBuildings && (card.cost || 0) >= 40)) {
      maxCopies = 1;
    }
    if ((card.cost ?? 0) < threshold) return false;
    const hand = this.hands[team];
    if (!hand) return false;
    let count = 0;
    for (let i = 0; i < hand.length; i++) {
      const c = hand[i];
      if (c && !c.pending && !c.hidden && c.name === card.name) {
        count++;
      }
    }
    return count >= maxCopies;
  }

  playCard(team, slotIndex, targetUnitId = null, options = {}) {
    const edition = options.edition || null;
    const customAuraHex = options.customAuraHex || null;
    const hand = this.hands[team];
    if (!hand) return false;
    const card = hand[slotIndex];
    this.lastPlayError = null;
    if (!card) return false;
    // Identity not resolved yet (online buffer underrun) - nothing to
    // spawn. update() will resolve it and the player can try again.
    if (card.pending) return false;

    const effectiveTeam = (team === 'w1' || team === 'w2' || team === 'western') ? 'western' : 'eastern';
    const isLocal = team === (this.game?.myRole || this.game?.myTeam || 'western');

    if (card.isBuilding) {
      const bs = this.battlefield.buildingSystem;
      if (!bs?.canBuild(effectiveTeam, card.buildingId)) {
        this.lastPlayError = bs?.has(effectiveTeam, card.buildingId)
          ? `${card.name} already built`
          : `Building capacity reached (${bs?.maxBuildings ?? 2}/${bs?.maxBuildings ?? 2})`;
        if (isLocal) this.audio?.playError?.();
        return false;
      }
    } else if (card.isModule || card.type === 'Module') {
      // Base module: extends the main building instead of spawning a Unit.
      // Rejected when it is already built/descending or the tower is full -
      // drawCard() above already keeps such a card out of the pool, this is
      // defense in depth for one drawn just before that became true.
      const ms = this.battlefield?.moduleSystem;
      if (!ms?.canBuild(effectiveTeam, card.moduleId)) {
        this.lastPlayError = ms?.isClaimed(effectiveTeam, card.moduleId)
          ? `${card.name} already built`
          : `Module stack full (${MODULE_MAX}/${MODULE_MAX})`;
        this.lastPlayErrorSlot = { team, slot: slotIndex };
        this.playErrorSeq = (this.playErrorSeq || 0) + 1;
        if (isLocal) this.audio?.playError?.();
        return false;
      }
    } else if ((card.isStructure || card.type === 'Structure') && this.isStructureAlreadyOnField(team, card)) {
      // General rule: a Structure card can't be played again while its team
      // already has a living instance of it on the field - see
      // isStructureAlreadyOnField() below. Drawing a second copy is already
      // excluded from the pool in drawCard() above; this is defense in
      // depth for one drawn into hand just before that became true.
      this.lastPlayError = `${card.name} already built`;
      if (isLocal) this.audio?.playError?.();
      return false;
    }

    const cost = this.getEffectiveCost(team, card);
    if (!this.economy.spendPlasma(team, cost)) {
      this.lastPlayError = 'Not enough Plasma gas';
      // Lets HUD trigger its one-shot "insufficient funds" shake on the
      // right slot even though the click that caused this and the tick that
      // actually rejects it (via CommandProcessor) are no longer the same
      // synchronous call - see js/commands/commandProcessor.js.
      this.lastPlayErrorSlot = { team, slot: slotIndex };
      this.playErrorSeq = (this.playErrorSeq || 0) + 1;
      if (isLocal) this.audio?.playError?.();
      return false;
    }

    hand[slotIndex] = null;
    this.battlefield?.stats?.recordCardPlayed(effectiveTeam, card.race);
    if (card.isSpell || card.type === 'Spell') {
      this.battlefield?.stats?.recordSpellPlayed(effectiveTeam);
    }
    if (card.isStructure || card.type === 'Structure' || card.isModule || card.type === 'Module' || card.isBuilding) {
      this.battlefield?.stats?.recordStructurePlayed(effectiveTeam);
    }
    if (card.isFlyer || card.isAir || (card.flags && card.flags.includes('Flyer'))) {
      this.battlefield?.stats?.recordAirPlayed(effectiveTeam);
    }
    if (isLocal) this.audio?.playCardPlay?.();

    if (card.isBuilding) {
      this.battlefield.buildingSystem.build(effectiveTeam, card.buildingId);
    } else if (card.isModule || card.type === 'Module') {
      // Starts the cable descent; HP and function take effect when it lands
      // (js/engine/modules.js). Deliberately after the plasma spend above, so
      // a module costs its plasma at the moment it is played, not on landing.
      this.battlefield.moduleSystem.build(effectiveTeam, card.moduleId, card);
      if (isLocal) this.audio?.playBark?.(card.race, 'spawn');
    } else if (card.isSpell) {
      this.executeSpell(card, effectiveTeam, targetUnitId, slotIndex);
    } else {
      this.battlefield.spawnUnit(card, effectiveTeam, edition, customAuraHex);
      if (isLocal) {
        if (card.name === 'The Salvage Company' || card.unitType === 'TheSalvageCompany') {
          this.audio?.playSalvageHorn?.();
        } else if (!hasCustomSpawnSound(card)) {
          this.audio?.playUnitSpawn?.(card);
        }
      }
    }

    return true;
  }

  // Dispatches to SPELL_HANDLERS by card.spellEffect (see the table and its
  // comment above the class) instead of an if/else chain - the ctx object
  // built here is the only thing a handler can see, which is what keeps
  // battlefield.selectedUnit from being one keystroke away.
  executeSpell(card, team, targetUnitId = null, originSlot = null) {
    const handler = SPELL_HANDLERS[card.spellEffect] || SPELL_HANDLERS._fallback;
    handler({
      team,
      targetUnitId,
      originSlot,
      cardSystem: this,
      economy: this.economy,
      battlefield: this.battlefield,
      particles: this.particles,
      audio: this.audio,
      rng: this.rng,
      discounts: this.discounts
    });
  }
}
